import { useEffect, useMemo, useState } from 'react';
import { CommonContentContext, IBackButtonConfig } from './CommonContentContext';
import { Modal } from 'antd';
import { UserRole } from '@/utils/interfaces/common/UserRole.enum';
import { useList, useNotification } from '@refinedev/core';
import BaseService from '@/services/common/BaseService';
import { FarmEssentialDto } from '@/utils/farm/dtos/farmEssentialDto';
import { captureAppException, captureAppMessage, getHttpErrorContext } from '@/utils/sentry/sentryLogger';

export const CommonContentProvider = ({ children }) => {
  const { open } = useNotification();
  const [userProfile, setUserProfile] = useState<UserRole>();
  const [showMenu, setShowMenu] = useState(false);
  const [navbarWithMenu, setNavbarWithMenu] = useState(false);
  const [disableElement, setDisableElement] = useState<boolean>(false);
  const [recordMachinesAndImplements, setRecordMachinesAndImplements] = useState<boolean>(false);
  const [tenantId, setTenantId] = useState<string | null>(null);
  const [farmId, setFarmId] = useState<number | null>(null);
  const [backButtonConfig, setBackButtonConfig] = useState<IBackButtonConfig>({ show: false });

  const {
    data: farmsData,
    isLoading: farmsIsLoading,
    refetch,
  } = useList<FarmEssentialDto>({
    resource: 'Farms/UserEmail',
    queryOptions: {
      enabled: false,
      onError: (error) => {
        captureAppException(error, {
          flow: 'app-startup',
          resource: 'Farms/UserEmail',
          tenantId: tenantId ?? undefined,
          ...getHttpErrorContext(error),
        });
      },
    },
  });

  useEffect(() => {
    if (farmsData && !farmsIsLoading) {
      if (farmsData.data.length > 0) {
        localStorage.setItem('FARM_ID', JSON.stringify(Number(farmsData.data[0].id)));
        setFarmId(Number(farmsData.data[0].id));
      } else {
        captureAppMessage('No farms returned for user', 'warning', {
          flow: 'app-startup',
          resource: 'Farms/UserEmail',
          tenantId: tenantId ?? undefined,
        });
        modalErrors(['No access to any farms. Please contact support for assistance, support@growdata.com.au']);
      }
    }
  }, [farmsData, farmsIsLoading, tenantId]);

  useEffect(() => {
    if (tenantId) {
      refetch();
    }
  }, [tenantId]);

  useEffect(() => {
    if (farmId) {
      BaseService.get('/Users/UserRole')
        .then((response) => {
          const user = response.data;
          setUserProfile(user.userProfileId);
        })
        .catch((error) => {
          captureAppException(error, {
            flow: 'app-startup',
            resource: 'Users/UserRole',
            tenantId: tenantId ?? undefined,
            farmId: farmId ?? undefined,
            ...getHttpErrorContext(error),
          });
          open?.({
            description: 'Failure',
            message: 'Failed to fetch user.',
            type: 'error',
          });
        });
    }
  }, [farmId]);

  const modalErrors = (errors: any) => {
    let errorList: string[] = [];

    if (Array.isArray(errors)) {
      errorList = errors;
    } else if (typeof errors === 'string') {
      errorList = [errors];
    } else if (errors && typeof errors === 'object') {
      if (errors.detail) {
        errorList = [errors.detail];
      } else if (errors.message) {
        errorList = [errors.message];
      } else {
        errorList = [JSON.stringify(errors)];
      }
    } else {
      errorList = ['An unexpected error occurred.'];
    }

    Modal.error({
      title: 'Error',
      content: (
        <div>
          {errorList.map((error, index) => {
            return (
              <div key={index}>
                {index > 0 && <br />}
                <p className="text-[15px] text-[#e83f3f]">{error}</p>
              </div>
            );
          })}
        </div>
      ),
      okButtonProps: { className: 'bg-primary' },
    });
  };

  const value = useMemo(
    () => ({
      userProfile,
      setUserProfile,
      showMenu,
      setShowMenu,
      navbarWithMenu,
      setNavbarWithMenu,
      disableElement,
      setDisableElement,
      recordMachinesAndImplements,
      setRecordMachinesAndImplements,
      modalErrors,
      tenantId,
      setTenantId,
      backButtonConfig,
      setBackButtonConfig,
      farmId,
      setFarmId,
    }),
    [
      userProfile,
      setUserProfile,
      showMenu,
      setShowMenu,
      navbarWithMenu,
      setNavbarWithMenu,
      disableElement,
      setDisableElement,
      recordMachinesAndImplements,
      setRecordMachinesAndImplements,
      modalErrors,
      tenantId,
      setTenantId,
      backButtonConfig,
      setBackButtonConfig,
      farmId,
      setFarmId,
    ]
  );

  return <CommonContentContext.Provider value={value}>{children}</CommonContentContext.Provider>;
};
